import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import usePublicPlans from '../../hooks/usePublicPlans';
import { employerPlans, formatPrice, FEATURED_PRICE } from '../../utils/plans';

const SIGNUP = '/register?type=employer';

const INCLUDED = [
  'Wnioski urlopowe od pracowników i akceptacja jednym kliknięciem',
  'Pula urlopowa każdego pracownika, liczona automatycznie',
  'Kalendarz urlopów całej firmy',
  'Odwoływanie zaakceptowanego urlopu',
  'Ogłoszenia o pracę i raporty',
];

const FAQ = [
  ['Czy darmowy pakiet jest bezterminowy?', 'Tak. Jeden pracownik jest za darmo bez limitu czasu i bez podawania karty.'],
  ['Co się stanie, gdy dojdę do limitu pracowników?', 'Kolejnego pracownika dodasz po wybraniu większego pakietu. Dotychczasowe dane i wnioski zostają.'],
  ['Co, jeśli nie przedłużę pakietu?', 'Konto działa dalej w darmowym pakiecie na jednego pracownika. Niczego nie usuwamy.'],
  ['Jak zapłacę?', 'Płatność odbywa się przez PayU, po zalogowaniu na konto firmy.'],
  ['Potrzebuję faktury VAT.', 'Napisz do nas przez formularz kontaktowy, podając dane firmy i NIP.'],
];

const durationLabel = (months) => (months > 1 ? `/ ${months} mies.` : '/ mies.');

function PlanCard({ title, price, period, details, featured, cta }) {
  return (
    <div className={`pub-card ${featured ? 'pub-card--featured' : ''}`}>
      <div className="pub-kicker" style={{ minHeight: 20 }}>{featured ? 'Najczęściej wybierany' : ''}</div>
      <h3 className="pub-h3">{title}</h3>
      <p style={{ margin: '12px 0 4px' }}>
        <span style={{ fontFamily: 'var(--pub-serif)', fontSize: 36, color: 'var(--pub-navy)' }}>{price}</span>{' '}
        <span className="pub-small">{period}</span>
      </p>
      <p className="pub-muted" style={{ minHeight: 52 }}>{details}</p>
      <Link to={SIGNUP} className={featured ? 'pub-btn' : 'pub-link'}>{cta}</Link>
    </div>
  );
}

function PublicPricing() {
  const { plans, loading, error } = usePublicPlans();
  const paid = employerPlans(plans);

  return (
    <>
      <Helmet>
        <title>Cennik – SIGMA | Wnioski urlopowe dla firm</title>
        <meta name="description" content="Zacznij za darmo z jednym pracownikiem. Pakiety dla firm na 10, 25 i 50 pracowników. Pracownicy korzystają z SIGMY bezpłatnie." />
      </Helmet>

      <section className="pub-section">
        <div className="pub-wrap">
          <div className="pub-kicker">Cennik</div>
          <h1 className="pub-h1">Zacznij za darmo. Płać, gdy zespół rośnie.</h1>
          <p className="pub-lead">Jeden pracownik jest za darmo, bez karty i bez limitu czasu. Większy pakiet wybierasz dopiero wtedy, gdy go potrzebujesz.</p>

          <div className="pub-plans">
            <PlanCard
              title="Darmowy"
              price="0 zł"
              period="bezterminowo"
              details="1 pracownik, wszystkie funkcje, bez karty."
              cta="Wypróbuj bezpłatnie"
            />
            {paid.map(p => (
              <PlanCard
                key={p.id}
                title={p.title || p.name}
                price={formatPrice(p.price)}
                period={durationLabel(p.duration_months)}
                details={p.worker_limit > 1 ? `Do ${p.worker_limit} pracowników, wszystkie funkcje.` : 'Wszystkie funkcje.'}
                featured={Number(p.price) === FEATURED_PRICE}
                cta="Wybierz pakiet"
              />
            ))}
          </div>

          {loading && <p className="pub-small" style={{ marginTop: 20 }}>Wczytywanie cen…</p>}
          {error && (
            <p className="pub-muted" style={{ marginTop: 20 }}>
              Nie udało się pobrać cen płatnych pakietów. Odśwież stronę albo zacznij od darmowego pakietu, który działa od razu.
            </p>
          )}
          <p className="pub-muted" style={{ marginTop: 28 }}>Pracownicy korzystają z SIGMY za darmo. Płaci tylko firma.</p>
        </div>
      </section>

      <section className="pub-section pub-section--alt">
        <div className="pub-wrap pub-split" style={{ alignItems: 'start' }}>
          <div>
            <h2 className="pub-h2">W każdym pakiecie</h2>
            <p className="pub-muted">Pakiety różnią się tylko liczbą pracowników. Funkcje są te same.</p>
          </div>
          <ul className="pub-list">
            {INCLUDED.map(item => <li key={item}>{item}</li>)}
          </ul>
        </div>
      </section>

      <section className="pub-section">
        <div className="pub-wrap">
          <h2 className="pub-h2">Pytania o płatności</h2>
          <div className="pub-faq">
            {FAQ.map(([q, a]) => (
              <details key={q}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
          <div className="pub-actions" style={{ marginTop: 40 }}>
            <Link to={SIGNUP} className="pub-btn">Wypróbuj bezpłatnie</Link>
            <Link to="/contact" className="pub-link">Masz pytanie? Napisz do nas</Link>
          </div>
        </div>
      </section>
    </>
  );
}

export default PublicPricing;
